import { type ReactElement, type ReactNode, useEffect, useMemo } from 'react';
import {
  CONTEXT_SELECTION_UPDATED_EVENT,
  getStoredContext,
  isStoredContextStorageEvent,
  updateSessionContext,
} from '@/auth/sessionStorage';
import { useContextSelection } from '@/shared/hooks/useContextSelection';
import { ProjectFilterContext } from './projectContext';
import type { ProjectFilterContextValue } from './projectTypes';

export const ProjectFilterProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const { value: selectedProjectId, setValue: setSelectedProjectId } = useContextSelection({
    getInitialValue: () => {
      if (typeof window === 'undefined') return null;
      const { lastProjectId } = getStoredContext();
      return lastProjectId;
    },
    onPersist: (value) => {
      updateSessionContext(undefined, value);
    },
  });

  const value = useMemo<ProjectFilterContextValue>(
    () => ({ selectedProjectId, setSelectedProjectId }),
    [selectedProjectId],
  );

  useEffect(() => {
    const syncFromStoredContext = (): void => {
      const { lastProjectId } = getStoredContext();
      setSelectedProjectId(lastProjectId);
    };
    const handleStorage = (event: StorageEvent): void => {
      if (!isStoredContextStorageEvent(event.key)) {
        return;
      }
      syncFromStoredContext();
    };
    const handleContextUpdate = (): void => {
      syncFromStoredContext();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(CONTEXT_SELECTION_UPDATED_EVENT, handleContextUpdate);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(CONTEXT_SELECTION_UPDATED_EVENT, handleContextUpdate);
    };
  }, [setSelectedProjectId]);

  return <ProjectFilterContext.Provider value={value}>{children}</ProjectFilterContext.Provider>;
};
